import { Component, Input } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { resourceTypes } from '@shared/constants/resourceTypes';
import { PrintingStatusReason } from '@shared/constants/status-reason';
import { statusTypes } from '@shared/constants/status-types';
import { IDevice } from '@shared/interfaces/device/device';
import { AddMoreResourcesModalComponent } from '@shared/modules/account/modals/add-more-resources-modal/add-more-resources-modal.component';
import { ConfirmationsModalComponent } from '@shared/modals/confirmations-modal/confirmations-modal.component';
import { PrintFileFromDeviceModalComponent } from '@shared/modules/print-file-shared/modals/print-file-from-device-modal/print-file-from-device-modal.component';
import { Confirmation } from '@shared/models/confirmation';
import { DeviceStatusService } from '@shared/modules/device/services/device-status.service';
import { AccessService } from '@access/access.service';
import { PERMISSIONS } from '@access/permissions';

@Component({
  selector: 'app-device-buttons',
  templateUrl: './device-buttons.component.html',
  styleUrls: ['./device-buttons.component.scss'],
})
export class DeviceButtonsComponent {
  @Input() device!: IDevice;
  resourceTypes = resourceTypes;
  statusTypes = statusTypes;

  readonly permissions = PERMISSIONS;

  constructor(
    private dialogService: NbDialogService,
    private statusService: DeviceStatusService,
    private readonly accessService: AccessService
  ) {}

  protected get canStopPrinting(): boolean {
    return this.statusService.checkReason(
      this.device.status,
      statusTypes.printing,
      [
        PrintingStatusReason.NO_REASON,
        PrintingStatusReason.PAUSED,
        PrintingStatusReason.HEATING,
      ]
    );
  }

  protected get canPause(): boolean {
    return this.statusService.checkReason(
      this.device.status,
      statusTypes.printing,
      [PrintingStatusReason.NO_REASON]
    );
  }

  protected get isPaused(): boolean {
    return this.statusService.checkReason(
      this.device.status,
      statusTypes.printing,
      PrintingStatusReason.PAUSED
    );
  }

  onCreatePrintFileFromDeviceModal() {
    if (!this.accessService.hasPermission(PERMISSIONS.Devices.Commands.Start)) {
      return;
    }

    this.dialogService
      .open<any>(PrintFileFromDeviceModalComponent, {
        context: { device: this.device }, //TODO
      })
      .onClose.subscribe((res) => {
        if (res && res.transferResources) {
          this.dialogService.open<any>(AddMoreResourcesModalComponent, {
            context: {
              resource: resourceTypes.transfer,
              currentCount: res.transferResources.currentTransfer,
              priceForOneUnit: res.transferResources.priceForOneUnit,
              totalCount: res.transferResources.totalTransfer,
              totalLimit: res.transferResources.totalTransferLimit,
              title: 'stats-and-resources.add-more-transfer',
              currency: res.transferResources.currency,
              isActive: res.isActive,
            },
          });
        }
      });
  }

  onCreateModalPause(id: string): void {
    if (!this.accessService.hasPermission(PERMISSIONS.Devices.Commands.Pause)) {
      return;
    }

    this.onCreateModalConfirmation(
      new Confirmation(
        'pause-circle-outline',
        'device.buttons.pause-confirmation-title',
        'device.buttons.pause-confirmation-message',
        'pausePrinting',
        'device.buttons.pause',
        'info'
      ),
      new DeviceInformationContent(id)
    );
  }

  onCreateModalStop(id: string): void {
    if (!this.accessService.hasPermission(PERMISSIONS.Devices.Commands.Stop)) {
      return;
    }

    this.onCreateModalConfirmation(
      new Confirmation(
        'stop-circle-outline',
        'device.buttons.stop-confirmation-title',
        'device.buttons.stop-confirmation-message',
        'stopPrinting',
        'device.buttons.stop',
        'danger'
      ),
      new DeviceInformationContent(id)
    );
  }

  onCreateModalResume(id: string): void {
    if (!this.accessService.hasPermission(PERMISSIONS.Devices.Commands.Resume)) {
      return;
    }

    this.onCreateModalConfirmation(
      new Confirmation(
        'play-circle-outline',
        'device.buttons.resume-confirmation-title',
        'device.buttons.resume-confirmation-message',
        'resumePrinting',
        'device.buttons.resume',
        'primary'
      ),
      new DeviceInformationContent(id)
    );
  }

  onCreateModalConfirmation(
    modalData: Confirmation,
    deviceData: DeviceInformationContent
  ): void {
    this.dialogService.open<any>(ConfirmationsModalComponent, {
      context: { modalData: modalData, deviceData: deviceData },
    });
  }
}

class DeviceInformationContent {
  id: string;

  constructor(id: string) {
    this.id = id;
  }
}
